import { useState } from 'react';

import { useNavigate } from 'react-router';

import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { useTranslation } from 'react-i18next';

import { PathwayUser, Plan } from '@cfa/pathway-fe-shared/xp';
import { Radio, RadioGroup } from '@cfa/react-core';

import { sendNativeMessage } from '@/hooks/useNativeListener';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';

import { TeamMembersStep } from '@/components/popups/AssignTeamMembersModal';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import PathwayModal from '@/components/popups/PathwayModal';

import Constants, { routes } from '@/constants';

import {
  trainTogetherModeAtom,
  trainTogetherModeDispatchAtom,
} from '@/atoms/trainTogetherMode';

const isOpenAtom = focusAtom(trainTogetherModeAtom, o =>
  o.prop('isTrainTogetherModeOpen'),
);
const setTrainTogetherModeDataAtom = focusAtom(trainTogetherModeAtom, o =>
  o.prop('trainTogetherModeData'),
);

export interface TrainTogetherModalProps {
  plan: Plan;
}

type Step = 'enter' | 'team-members' | 'language';

const TrainTogetherModal: React.FC<TrainTogetherModalProps> = ({ plan }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isOpen = useAtomValue(isOpenAtom);
  const sendTrainTogetherModeAction = useSetAtom(trainTogetherModeDispatchAtom);
  const setTrainTogetherModeData = useSetAtom(setTrainTogetherModeDataAtom);

  const [step, setStep] = useState<Step>('enter');
  const [temporaryTeamMembers, setTemporaryTeamMembers] = useState<
    PathwayUser[]
  >([]);
  const [languageSelected, setLanguageSelected] = useState(
    Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE,
  );

  const planId = plan.id;

  const handleClose = () => {
    setStep('enter');
    setTemporaryTeamMembers([]);
    setLanguageSelected(Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE);
    sendTrainTogetherModeAction('hideTrainTogetherMode');
  };

  const handleEnterNext = () => {
    setStep('team-members');
  };

  const handleTeamMembersNext = ({
    selectedTeamMembers,
  }: {
    selectedTeamMembers: PathwayUser[];
    id?: string;
    autoAssign: boolean;
  }) => {
    setTemporaryTeamMembers(selectedTeamMembers);
    setStep('language');
  };

  const handleLanguageNext = () => {
    sendTrainTogetherModeAction('enterTrainTogetherMode');
    sendTrainTogetherModeAction('hideTrainTogetherMode');
    sendTrainTogetherModeAction('hideProgressModal');
    sendNativeMessage({ type: 'trainTogetherModeActive', payload: true });
    setTrainTogetherModeData({
      estimatedMinutes: plan.estimatedTimeToCompleteInMinutes ?? 0,
      languageSelected,
      planId,
      planLabel: t(mapCategoryToAliasTranslation(plan.category)),
      planName: itemByLanguage(plan.name, ''),
      selectedTeamMemberIds: temporaryTeamMembers
        .map(member => member.adId)
        .filter((id): id is string => !!id),
    });
    setStep('enter');
    navigate({ pathname: routes.plans.trainTogether({ planId }) });
  };

  return (
    <PathwayModal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      stepKey={step}
    >
      {step === 'enter' && (
        <ConfirmationModal
          isStep
          headerText={t('TrainingPlans.trainTogether.enterModalHeader')}
          bodyText={t('TrainingPlans.trainTogether.enterModalBody')}
          primaryButtonText={t('Button.iUnderstand')}
          primaryButtonHandler={handleEnterNext}
          secondaryButtonText={t('Button.cancel')}
          secondaryButtonHandler={handleClose}
          secondaryButtonColor="secondary"
        />
      )}
      {step === 'team-members' && (
        <TeamMembersStep
          isOpen={isOpen}
          isTrainTogetherMode
          onClose={handleClose}
          plan={plan}
          onNext={handleTeamMembersNext}
        />
      )}
      {step === 'language' && (
        <ConfirmationModal
          isStep
          headerText={t('TrainingPlans.trainTogetherMode.chooseLanguage')}
          bodyText={t('TrainingPlans.trainTogetherMode.selectLanguage')}
          primaryButtonText={t('Button.next')}
          primaryButtonHandler={handleLanguageNext}
          secondaryButtonText={t('Button.back')}
          secondaryButtonHandler={() => setStep('team-members')}
          secondaryButtonColor="secondary"
        >
          <div className="flex cursor-pointer justify-center">
            <RadioGroup
              defaultValue={Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE}
              orientation="vertical"
              onChange={value => setLanguageSelected(value)}
            >
              <Radio value={Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE}>
                {t('Language.english')}
              </Radio>
              <Radio value={Constants.LANGUAGE.SPANISH_LANGUAGE_CODE}>
                {t('Language.spanish')}
              </Radio>
            </RadioGroup>
          </div>
        </ConfirmationModal>
      )}
    </PathwayModal>
  );
};

export default TrainTogetherModal;
